import type { CSSObject, Theme } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';

import { spacing } from '@verantos-dev/shared-ui-components/tokens';

/** Field of the collapsed column that carries the checkboxes. Holds no data. */
export const ARTIFACT_SELECTION_FIELD = '__artifactSelection__';

// Not 0: MUI resolves width as `column.width || DEFAULT`, so a falsy 0 becomes the
// 100px default. `selectionColumnSx` cancels this pixel with a negative margin.
export const SELECTION_COLUMN_WIDTH = 1;

// Leading gutter the Name column reserves for the checkbox, sized to the control.
export const SELECT_SLOT_INSET = spacing[2];
export const LEADING_SLOT_SIZE = 22;
const LEADING_SLOT_GAP = spacing[1];
export const NAME_LEADING_INSET = SELECT_SLOT_INSET + LEADING_SLOT_SIZE + LEADING_SLOT_GAP;

/** Applied to the checkbox wrapper, in the header and rows. */
export const SELECT_CONTROL_CLASS = 'artifact-select-control';

/** Grid-root attribute so one CSS rule can hold every checkbox open. */
export const SELECTION_MODE_ATTR = 'data-selection-mode';

const selectionCell = `& .${gridClasses.cell}[data-field="${ARTIFACT_SELECTION_FIELD}"]`;
const selectionHeader = `& .${gridClasses.columnHeader}[data-field="${ARTIFACT_SELECTION_FIELD}"]`;

/** Reserved on every tab of a selectable grid, so tabs don't shift the names. */
export const nameLeadingSlotSx: CSSObject = {
  [`& .${gridClasses.cell}[data-field="name"],
    & .${gridClasses.columnHeader}[data-field="name"]`]: {
    paddingLeft: `${NAME_LEADING_INSET}px`,
  },
};

/** Collapses the selection column. Applied to the root only while selectable. */
export const selectionColumnSx = (theme: Theme): CSSObject => ({
  // A 1px cell clips its contents and draws a separator; the negative margin pulls
  // the following columns back over that pixel.
  [`${selectionCell}, ${selectionHeader}`]: {
    position: 'relative',
    overflow: 'visible',
    padding: 0,
    margin: 0,
    marginRight: `-${SELECTION_COLUMN_WIDTH}px`,
    minWidth: 0,
    border: 'none',
    outline: 'none',
  },
  // Would otherwise be a stray tick at the grid's left edge.
  [`${selectionHeader} .${gridClasses.columnSeparator}`]: { display: 'none' },

  // Parked in the Name gutter. `opacity` not `visibility`/`display` — those drop it
  // from the a11y tree, making the `:focus-within` reveal unreachable by keyboard.
  [`& .${SELECT_CONTROL_CLASS}`]: {
    position: 'absolute',
    left: `${SELECT_SLOT_INSET}px`,
    top: '50%',
    transform: 'translateY(-50%)',
    width: LEADING_SLOT_SIZE,
    height: LEADING_SLOT_SIZE,
    // The Name cell is painted after this one and overlaps the gutter.
    zIndex: 1,
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  },

  // Hover or keyboard focus brings a row's checkbox up.
  [`& .${gridClasses.row}:hover .${SELECT_CONTROL_CLASS},
    & .${gridClasses.columnHeaders}:hover .${SELECT_CONTROL_CLASS},
    & .${gridClasses.row}:focus-within .${SELECT_CONTROL_CLASS},
    & .${gridClasses.columnHeaders}:focus-within .${SELECT_CONTROL_CLASS}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },

  // Once anything is picked, every checkbox stays up — refused rows included.
  [`&[${SELECTION_MODE_ATTR}='on'] .${SELECT_CONTROL_CLASS}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
});
